// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Company.js
// Company info page: /company?ticker=BHP:AU
// Overview panel (current AI health check, price, performance vs sector,
// fundamentals / ETF facts, earnings, dividend, top holdings) above the
// announcements table (metadata | AI summary) for ASX and SEC listings.
// The health check is the newest one written by the analysis stage - each
// analysed announcement updates it; older ones stay in the database.
// Admins queue "Get AI summary" on the server. The global queue works across
// companies, oldest announcement first, and survives leaving the page.
// /company/queue supplies durable status and completed summaries every 5s. The health check is laid out
// in labelled parts ("Bottom line:", "Key facts ...:") - labels shown in bold.
// Admins get an Update button under Woody Bot's AI Summary that re-runs the health
// check (POST /company/health) and shows the new one when it's written.
// Admins also get a "Push to Discord" button under each AI summary (DiscordPush),
// and Re-summarise: redoes the summary with the live prompt and the company's
// history (POST /admin/audit/resummarise - health check untouched), then polls
// /company/analysis until summary_at changes and shows the new summary.
// Admins also see, under a summary, the earlier title-only announcements the AI
// said it needed (reading, matched in needs_reading.py) with a Summarise button
// each (the same /company/analyse as Get AI summary). Once one is summarised the
// summary that needed it is flagged "history changed"; the header's
// "Re-summarise stale" button redoes every flagged summary on the page.
// Summaries written with the company's history also carry a novelty tag
// (NoveltyTag, beside the dots) and a "compared with before" line (vs_before).
// Then the expected move and surprise (ImpactTags), and an "Expected:" line (surprise_basis).
// Thematics (ThematicTags, after the novelty tag) are the labels from the admin
// Thematics prompt that the AI matched to the announcement.
// Header: Chart ↗ beside the company name (new chart tab); right-hand ticker
// search (as Announcements.js / Chart.js - /chart/search) selects a company.
// Admins get an Audit button under each summary: the AI Audit page's popup
// (components/AuditPanel.js) for that row, its audits from /admin/audit/for.
// Audits started here are ordinary audit rows, so the overall assessment picks them up.
// Sensitive column: ● = ASX's official price-sensitive flag; ○ = SEC filing
// that is material by its type (sec_forms.is_material - our rule, not the SEC's).
// Units: growth/drip and dividend yield already % (snapshot_equities, written by
// calc_performance.py); margins and ROE fractions; MER, TTM yield and holding
// weights already % (EtfScreener.js).
// Header strip (Tiles): key numbers as tiles plus a 52-week range bar, above
// the chart; it replaced the price in the page header's top-right corner.
// Analyst targets tile: band from the lowest to the highest current target,
// (clicking it opens /analysts filtered to this ticker),
// a tick at the average, a dot at the price, and a Buy / Hold / Sell strip
// counting each firm once (its latest rating) - all investing.com data.
// Performance (PerfTiles): one tile per period - the company's return large,
// the sector median under it and a beat / lagged tag. Toggle between total
// return (dividends reinvested) and price only.
// Health card (HealthBody): the check's labelled parts laid out - bottom line
// as the lead, key facts as a bullet list,
// strength and risk as green / red callouts, market figures as a footnote.
// A check without the labels (older free-form ones) falls back to plain text.
// 12-month price line (PriceLine): a plain SVG drawn here - no chart library.
// Closes come from /chart/data, the chart page's route (split-adjusted by the
// market.bars_eod_adj view; bars_eod itself is raw), trimmed to the last 12 months. Hovering also
// shows the market cap that day: the overview's `shares` (market_caps - ASX
// daily, SEC per filing) in force on that date x the close. Days before the
// first known count show no market cap.
// P/E, EPS, margin, ROE, P/B and book value plus the 4-year revenue & profit table
// are our own, from reported figures: ASX key statistics (AU) or SEC 10-Ks (US) -
// company.py _ratios. No EODHD: without reported figures they show "—".
// Layout, table styling and navbar follow FdaApprovals.js / Chart.js.
// Below MOBILE_BP (same 768px breakpoint as Workspace.js / ScansWorkspace.js)
// the table becomes a list of cards, one per announcement.
// Route is wrapped in ProtectedRoute in App.js; the API is @login_required.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import DiscordPush from '../components/DiscordPush';
import NoveltyTag from '../components/NoveltyTag';
import ImpactTags from '../components/ImpactTags';
import ReactionTag from '../components/ReactionTag';
import ThematicTags from '../components/ThematicTags';
import AnalysisToggle from '../components/AnalysisToggle';
import CompanyQuestions from '../components/CompanyQuestions';
import { AuditPanel, AuditResult } from '../components/AuditPanel';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { Bot } from 'lucide-react';   // same icon as the dashboard's AI Plane card

const PAGE_SIZE = 100;
const POLL_MS   = 5000;              // check for a finished summary every 5s
const SOURCE_LABELS = { asx: 'ASX', sec: 'EDGAR', bw: 'Business Wire', gnw: 'GlobeNewswire', prn: 'PR Newswire', anw: 'ACCESS Newswire', nfc: 'Newsfile', dod: 'Dept of War', insider: 'Insider buying' };   // market.announcements.source → shown name
const label = s => SOURCE_LABELS[s] || s;
const POLL_MAX  = 10 * 60 * 1000;    // give up after 10 minutes
const MOBILE_BP = 768;
const SEARCH_DEBOUNCE = 300;         // as Chart.js
const AUDIT_POLL_MS   = 10 * 1000;   // running audits, as AdminAudit.js

const TABLE_STYLES = `
  .co-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .co-table th {
    position: sticky; top: 0; z-index: 1;
    background: var(--surface);
    font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--text-muted);
    text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  .co-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); vertical-align: top; }
  .co-table tbody tr { transition: background 0.12s; }
  /* Row tint from the AI direction; unanalysed rows stay white. Hover darkens the tint a shade. */
  .co-table tbody tr.dir-positive, .co-card.dir-positive { background: var(--pos-faint); }
  .co-table tbody tr.dir-negative, .co-card.dir-negative { background: var(--neg-faint); }
  .co-table tbody tr.dir-mixed,    .co-card.dir-mixed    { background: var(--mix-faint); }
  .co-table tbody tr:hover              { background: var(--bg); }
  .co-table tbody tr.dir-positive:hover { background: var(--pos-hover); }
  .co-table tbody tr.dir-negative:hover { background: var(--neg-hover); }
  .co-table tbody tr.dir-mixed:hover    { background: var(--mix-hover); }
  .co-table .cell-muted  { color: var(--text-muted); }
  .co-table .cell-small  { font-size: 11px; color: var(--text-muted); white-space: nowrap; }
  .co-table .cell-ps     { text-align: center; color: var(--accent); }
  .cell-link { color: var(--text); text-decoration: none; }
  .cell-link:hover { color: var(--accent); text-decoration: underline; }
  .co-badge {
    display: inline-block; font-family: 'JetBrains Mono', monospace; font-size: 10px;
    padding: 1px 6px; border-radius: var(--radius); border: 1px solid var(--border);
    color: var(--text-muted); margin-right: 6px; white-space: nowrap;
  }
  .co-badge.sensitive { border-color: var(--accent); color: var(--accent); }
  .co-badge.material  { border-style: dashed; border-color: var(--accent); color: var(--accent); }
  .co-badge.via-link  { cursor: pointer; }
  .co-badge.via-link:hover { border-color: var(--accent); color: var(--accent); }

  /* Overview panel */
  .co-grid  { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 12px; margin-bottom: 20px; }
  .co-block { background: var(--surface); border: 1px solid var(--border); border-radius: calc(var(--radius) * 2); padding: 14px 16px; }
  .co-block.wide { grid-column: 1 / -1; }
  .co-block { min-width: 0; }
  .co-scroll { overflow-x: auto; }
  .co-note { font-size: 11px; color: var(--text-muted); }
  .co-block h4 {
    margin: 0 0 10px; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase; color: var(--text-muted);
  }
  .co-kv { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; padding: 3px 0; }
  .co-kv span:first-child { color: var(--text-muted); }
  .co-kv span:last-child  { text-align: right; }
  .co-perf { width: 100%; border-collapse: collapse; font-size: 13px; }
  .co-perf th { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); font-weight: 500; text-align: right; padding: 4px 8px; }
  .co-perf td { text-align: right; padding: 4px 8px; white-space: nowrap; }
  .co-perf td:first-child, .co-perf th:first-child { text-align: left; padding-left: 0; color: var(--text-muted); }
  .co-pos { color: var(--success); font-weight: 600; }
  .co-neg { color: var(--error); font-weight: 600; }
  .co-desc { font-size: 13px; color: var(--text-muted); margin-top: 8px; cursor: pointer; }
  .co-health { background: var(--accent-faint); border: 1px solid var(--accent-dim); border-radius: calc(var(--radius) * 2); padding: 14px 16px; margin-top: 16px; font-size: 13px; line-height: 1.5; }
  .co-health h4 { margin: 0 0 6px; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); }
  .co-health h4.co-h-title { display: flex; align-items: center; gap: 6px; }
  .co-health .meta { font-size: 11px; color: var(--text-muted); margin-top: 6px; }
  .co-health .body { white-space: pre-line; }        /* fallback: the check's parts and bullets are separate lines */
  .co-h-lead { font-size: 15px; font-weight: 600; line-height: 1.45; margin: 2px 0 8px; }
  .co-h-facts { margin-top: 12px; }
  .co-h-facts ul { margin: 4px 0 0; padding-left: 18px; }
  .co-h-facts li { margin: 2px 0; }
  .co-calls  { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 8px; margin-top: 14px; }
  .co-call   { border-radius: calc(var(--radius) * 2); padding: 10px 12px; border-left: 3px solid; background: var(--surface); }
  .co-call b { display: block; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 500; letter-spacing: 0.1em;
               text-transform: uppercase; margin-bottom: 4px; }
  .co-call.pos   { border-color: var(--success); background: var(--pos-faint); }
  .co-call.pos b { color: var(--success); }
  .co-call.neg   { border-color: var(--error); background: var(--neg-faint); }
  .co-call.neg b { color: var(--error); }
  .co-call.watch   { border-color: var(--accent); }
  .co-call.watch b { color: var(--accent); }
  .co-h-foot { font-size: 11px; color: var(--text-muted); margin-top: 12px; }
  .co-context { display: block; margin-top: 4px; font-size: 12px; font-style: italic; color: var(--text-muted); }
  .co-vs      { display: block; margin-top: 4px; font-size: 12px; color: var(--text-muted); }
  .co-read    { display: block; margin-top: 6px; font-size: 11px; color: var(--text-muted); }
  .co-read div { margin-top: 2px; }
  .co-stale   { display: inline-block; margin-left: 6px; vertical-align: middle; font-family: 'JetBrains Mono', monospace; font-size: 9px;
                padding: 0 5px; border-radius: var(--radius); background: var(--warning-dim); color: var(--warning); white-space: nowrap; }
  .co-caveat  { display: block; margin-top: 6px; font-size: 11px; color: var(--text-muted); }
  .co-aimodel { display: block; margin-top: 4px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); }
  .co-aibtn   { font-family: 'Space Grotesk', sans-serif; font-size: 11px; padding: 3px 10px; cursor: pointer;
                border: 1px solid var(--accent); border-radius: var(--radius); background: var(--surface); color: var(--accent); }
  .co-aibtn:hover { background: var(--accent-dim); }
  .co-aiwait  { font-size: 11px; color: var(--accent); }
  .co-upd { margin-left: 10px; vertical-align: middle; font-family: 'Space Grotesk', sans-serif; font-size: 11px; font-weight: 400;
            padding: 2px 8px; cursor: pointer; border: 1px solid var(--accent); border-radius: var(--radius);
            background: var(--surface); color: var(--accent); }
  .co-upd:hover:not(:disabled) { background: var(--accent-dim); }
  .co-upd:disabled { cursor: default; opacity: 0.8; }
  .co-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; margin-top: 16px; }
  .co-tile  { background: var(--surface); border: 1px solid var(--border); border-radius: calc(var(--radius) * 2); padding: 10px 14px; }
  .co-tile.range { grid-column: span 2; }
  .co-tile .k { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text-muted); }
  .co-tile .v { font-size: 18px; font-weight: 600; margin-top: 4px; white-space: nowrap; }
  .co-tile .s { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .co-range { position: relative; height: 6px; border-radius: 3px; background: var(--border); margin: 12px 0 6px; }
  .co-range .fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 3px; background: var(--accent-dim); }
  .co-range .dot  { position: absolute; top: -3px; width: 12px; height: 12px; border-radius: 50%; transform: translateX(-50%);
                    background: var(--accent); border: 2px solid var(--surface); }
  .co-range-ends { display: flex; justify-content: space-between; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); }
  .co-tile .k.split { display: flex; justify-content: space-between; }
  .co-range .band { position: absolute; top: 0; bottom: 0; border-radius: 3px; background: var(--accent-dim); }
  .co-range .tick { position: absolute; top: -4px; width: 2px; height: 14px; transform: translateX(-50%); background: var(--accent); }
  .co-range .dot.price { background: var(--text); }
  .co-votes { display: flex; height: 4px; border-radius: 2px; overflow: hidden; margin-top: 8px; background: var(--border); }
  .co-votes-key { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
  .co-pb-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
  .co-pb-head h4 { margin: 0; }
  .co-pb-mode button { font-family: 'Space Grotesk', sans-serif; font-size: 11px; padding: 2px 8px; cursor: pointer;
                       border: 1px solid var(--border); background: var(--surface); color: var(--text-muted); }
  .co-pb-mode button:first-child { border-radius: var(--radius) 0 0 var(--radius); }
  .co-pb-mode button:last-child  { border-radius: 0 var(--radius) var(--radius) 0; border-left: none; }
  .co-pb-mode button.on { background: var(--accent-dim); color: var(--accent); border-color: var(--accent); }
  .co-pb-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 10px; }
  .co-pb-grid .co-tile .v { font-size: 20px; }
  .co-tag { display: inline-block; margin-top: 6px; font-family: 'JetBrains Mono', monospace; font-size: 10px;
            padding: 1px 6px; border-radius: var(--radius); }
  .co-tag.beat { background: var(--pos-faint); color: var(--success); }
  .co-tag.lag  { background: var(--neg-faint); color: var(--error); }
  .co-chart { margin-top: 16px; }
  .co-chart h4 { display: flex; justify-content: space-between; }
  .co-chart svg { display: block; touch-action: pan-y; }   /* sideways drag moves the cursor, vertical still scrolls */
  .co-chart .axis { font-family: 'JetBrains Mono', monospace; font-size: 10px; fill: var(--text-muted); }
  .co-desc.clamped { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

  .co-fold { width: 100%; padding: 2px 0; display: flex; align-items: center;
             justify-content: space-between; gap: 12px; cursor: pointer;
             background: none; border: 0; color: var(--text);
             font-family: 'Space Grotesk', sans-serif; font-size: 14px; font-weight: 600; text-align: left; }
  .co-fold span:last-child { color: var(--accent); font-size: 12px; font-weight: 500; white-space: nowrap; }
  /* Importance dots and the key above the table */
  .sig-dots { display: inline-flex; gap: 2px; vertical-align: middle; white-space: nowrap; }
  .sig-dots i { display: inline-block; width: 7px; height: 7px; border-radius: 50%; border: 1px solid currentColor; opacity: 0.35; }
  .sig-dots i.on { background: currentColor; opacity: 1; }
  .sig-key { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 18px; font-size: 11px; color: var(--text-muted); margin-bottom: 8px; }
  .sig-key > span { display: inline-flex; align-items: center; gap: 6px; }
  .sig-key .sw { display: inline-block; width: 12px; height: 12px; border-radius: 3px; border: 1px solid var(--border); }
  .sig-key .sw:not(:first-child) { margin-left: 10px; }
  /* Mobile cards */
  .co-card { padding: 12px 14px; border-bottom: 1px solid var(--border); font-size: 13px; }
  .co-card:last-child { border-bottom: none; }
  .co-card .card-top  { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 0; margin-bottom: 6px; }
  .co-card .card-when { font-size: 11px; color: var(--text-muted); margin-right: 8px; }
  .co-card .card-meta { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
  .co-card .card-sum  { font-size: 12px; color: var(--text-muted); margin-top: 8px; }
  .co-card .card-sum b { color: var(--text); font-weight: 600; }
  /* Ticker search - same look as the Announcements / Chart.js search box. */
  .co-search { position: relative; width: 240px; }
  .co-search input { width: 100%; box-sizing: border-box; padding: 5px 10px; font-size: 12px; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.05em;
                     border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); color: var(--text); outline: none; }
  .co-search input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-dim); }
  .co-drop { position: absolute; top: calc(100% + 4px); right: 0; width: 340px; max-width: 90vw; background: var(--surface); border: 1px solid var(--border);
             border-radius: var(--radius); box-shadow: var(--shadow); z-index: 100; max-height: 280px; overflow-y: auto; }
  .co-drop div { padding: 8px 12px; cursor: pointer; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid var(--border); }
  .co-drop div:hover { background: var(--bg); }
  .co-drop .sym  { font-family: 'JetBrains Mono', monospace; font-weight: 500; flex-shrink: 0; }
  .co-drop .nm   { font-size: 12px; color: var(--text-muted); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .co-drop .bdg  { font-size: 10px; font-family: 'JetBrains Mono', monospace; padding: 2px 6px; border-radius: 3px; font-weight: 600; flex-shrink: 0; }
  .co-drop .bdg.AU  { background: var(--info-dim); color: var(--info); }
  .co-drop .bdg.US  { background: var(--warning-dim); color: var(--warning); }
  .co-drop .bdg.etf { background: var(--accent-dim); color: var(--accent); }
`;

const PAGE_STYLES = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '32px' },
  header: { marginBottom: '20px' },
  title: { fontSize: '20px', fontWeight: 600, letterSpacing: '-0.01em' },
  sub: { fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' },
  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', overflow: 'auto', maxHeight: 'calc(100vh - 240px)' },
  stateBox: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' },
  errorBox: { background: 'var(--surface)', border: '1px solid var(--error)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', color: 'var(--error)', fontSize: '13px', marginBottom: '16px' },
  moreBtn: {
    marginTop: '12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    color: 'var(--text)', fontSize: '12px', padding: '6px 14px', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
  },
};

// Browser-local date and time, e.g. "21 Sep 2026, 08:35".
function fmtWhen(iso) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Number formatting for the overview panel. "—" whenever a value is missing.
const num  = (v, dp = 2) => v == null ? '—' : Number(v).toFixed(dp);
// Prices: 2 decimals (3 under $1, so penny stocks don't show 0.00), with thousands separators.
const price = v => {
  if (v == null) return '—';
  const dp = Math.abs(v) < 1 ? 3 : 2;
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};
const pctV = v => v == null ? '—' : `${Number(v).toFixed(2)}%`;   // already %
function big(v) {                                                  // 1.2B / 340M
  if (v == null) return '—';
  const a = Math.abs(v);
  return a >= 1e12 ? `${(v / 1e12).toFixed(2)}T` : a >= 1e9 ? `${(v / 1e9).toFixed(2)}B`
       : a >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : a >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : Number(v).toFixed(0);
}
const day = iso => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-GB',
  { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Signed % coloured green / red.
function Signed({ v }) {
  if (v == null) return <span className="cell-muted">—</span>;
  return <span className={v >= 0 ? 'co-pos' : 'co-neg'}>{v >= 0 ? '+' : ''}{Number(v).toFixed(2)}%</span>;
}

const KV = ({ k, v }) => <div className="co-kv"><span>{k}</span><span>{v}</span></div>;

// Overview panel. Blocks sit in an auto-fit grid, so they stack on mobile;
// the performance table scrolls sideways inside its block if it has to.
// Health check text with each part's label ("Bottom line:" etc.) in bold.
const HEALTH_LABEL = /^(Bottom line|How it's going|Key facts[^:]*|Main strength|Main risk|Watch for|Market figures[^:]*):/;
const healthText = text => text.split('\n').map((line, i) => {
  const m = line.match(HEALTH_LABEL);
  return <React.Fragment key={i}>{i > 0 && '\n'}{m ? <><b>{m[0]}</b>{line.slice(m[0].length)}</> : line}</React.Fragment>;
});

// ── Analyst targets tile ────────────────────────────────────────────────────
const VOTE_COLOURS = { Buy: 'var(--success)', Hold: 'var(--text-muted)', Sell: 'var(--error)' };

function AnalystTile({ a, close, ticker }) {
  const has = a.low != null && a.high != null && close != null;
  const pts = has ? [a.low, a.high, close, a.mean].filter(v => v != null) : [0];   // the mean comes from a different table, so it can sit outside low-high
  const lo = Math.min(...pts), hi = Math.max(...pts);
  const at = v => hi > lo ? (v - lo) / (hi - lo) * 100 : 50;          // % along the bar
  const votes = Object.entries(a.counts), total = votes.reduce((t, [, n]) => t + n, 0);
  return (
    <Link to={`/analysts?q=${encodeURIComponent(ticker)}`} className="co-tile range" style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
          title={`${a.d ? `Average of all current analyst 12-month targets, as at ${day(a.d)}. ` : ''}Click for every analyst's target`}>
      <div className="k split"><span>Analyst targets ↗</span>{a.upside != null && <span><Signed v={a.upside} /> to avg</span>}</div>
      {has && (
        <>
          <div className="co-range">
            <div className="band" style={{ left: `${at(a.low)}%`, width: `${at(a.high) - at(a.low)}%` }} />
            {a.mean != null && <div className="tick" style={{ left: `${at(a.mean)}%` }} />}
            <div className="dot price" style={{ left: `${at(close)}%` }} title={`Price ${price(close)}`} />
          </div>
          <div className="co-range-ends"><span>{price(a.low)} low</span><span>avg {price(a.mean)}</span><span>{price(a.high)} high</span></div>
        </>
      )}
      {total > 0 && (
        <>
          <div className="co-votes">
            {votes.map(([k, n]) => n > 0 && <div key={k} style={{ width: `${n / total * 100}%`, background: VOTE_COLOURS[k] }} />)}
          </div>
          <div className="co-votes-key">
            {votes.map(([k, n], i) => <span key={k}>{i > 0 && ' · '}<span style={{ color: VOTE_COLOURS[k] }}>{k} {n}</span></span>)}
            {' '}({total} firm{total === 1 ? '' : 's'})
          </div>
        </>
      )}
    </Link>
  );
}

// ── Header strip ──────────────────────────────────────────────────────────────
const Tile = ({ k, v, s }) => (
  <div className="co-tile"><div className="k">{k}</div><div className="v">{v}</div>{s && <div className="s">{s}</div>}</div>
);

function Tiles({ o }) {
  const pr = o.price, f = o.fundamentals, etf = o.etf, isEtf = o.asset_type === 'etf';
  const { low_52w: lo, high_52w: hi, close } = pr;
  const pos = lo != null && hi > lo && close != null ? Math.max(0, Math.min(100, (close - lo) / (hi - lo) * 100)) : null;
  return (
    <div className="co-tiles">
      <Tile k="Price" v={price(close)} s={<><Signed v={pr.change} /> · {day(pr.d)}</>} />
      {pos != null && (
        <div className="co-tile range">
          <div className="k">52-week range</div>
          <div className="co-range" title={`${num(pos, 0)}% of the way from the low to the high`}>
            <div className="fill" style={{ width: `${pos}%` }} />
            <div className="dot" style={{ left: `${pos}%` }} />
          </div>
          <div className="co-range-ends"><span>{price(lo)}</span><span><Signed v={pr.from_high} /> from high</span><span>{price(hi)}</span></div>
        </div>
      )}
      {isEtf ? (
        <>
          <Tile k="AUM (USD)" v={big(etf.aum_usd)} />
          <Tile k="MER" v={pctV(etf.expense_ratio)} />
          <Tile k="TTM yield" v={pctV(etf.ttm_yield)} />
        </>
      ) : (
        <>
          <Tile k="Market cap" v={big(f.market_cap)} s={f.shares ? `${big(f.shares)} shares` : undefined} />
          <Tile k="P/E" v={num(f.reported?.pe)} />
          <Tile k="Dividend yield" v={pctV(o.dividend.yield)} />
          {o.analysts && <AnalystTile a={o.analysts} close={close} ticker={o.ticker} />}
        </>
      )}
    </div>
  );
}

// ── Performance tiles ───────────────────────────────────────────────────────
const pctTxt = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

function PerfTiles({ o }) {
  const [mode, setMode] = useState('drip');                   // 'drip' total return | 'growth' price only
  const hasSector = o.performance.some(r => r.sector_growth != null);
  return (
    <div className="co-block wide">
      <div className="co-pb-head">
        <h4>Performance{hasSector ? ` · vs ${o.sector} median (${o.sector_peers} companies)` : ''}</h4>
        <div className="co-pb-mode">
          <button className={mode === 'drip' ? 'on' : ''} onClick={() => setMode('drip')}
                  title="Price change plus dividends reinvested">Total return</button>
          <button className={mode === 'growth' ? 'on' : ''} onClick={() => setMode('growth')}>Price only</button>
        </div>
      </div>
      <div className="co-pb-grid">
        {o.performance.map(r => {
          const v = r[mode], sv = hasSector ? r[`sector_${mode}`] : null;
          const both = v != null && sv != null;
          return (
            <div key={r.period} className="co-tile">
              <div className="k">{r.period}</div>
              <div className="v"><Signed v={v} /></div>
              {hasSector && <div className="s">Sector {pctTxt(sv)}</div>}
              {both && <span className={`co-tag ${v >= sv ? 'beat' : 'lag'}`}>{v >= sv ? 'beat sector' : 'lagged sector'}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 12-month price line ─────────────────────────────────────────────────────
const CHART_H = 220;
const PAD = { l: 4, t: 22, b: 20 };                 // right padding is sized to the price labels
const CHAR_W = 6.2;                                  // JetBrains Mono 10px, px per character
const YEAR_MS = 365 * 864e5;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const isoDay = ts => new Date(ts).toISOString().slice(0, 10);   // bars are UTC midnight

function PriceLine({ ticker, shares }) {
  const [pts, setPts] = useState(null);             // [[ts, close, adj_factor], ...]; [] = none
  const [w, setW] = useState(0);
  const [hover, setHover] = useState(null);         // index into pts
  const box = useRef(null);

  useEffect(() => {
    setPts(null); setHover(null);
    api.get(`/chart/data?ticker=${encodeURIComponent(ticker)}`)
      .then(r => {
        const d = r.data.ohlcv, f = r.data.adj_factor || [], end = d[d.length - 1][0];
        setPts(d.map((b, i) => [b[0], b[4], f[i] || 1])
                .filter(b => b[0] >= end - YEAR_MS && b[1] != null).map(b => [b[0], Number(b[1]), b[2]]));
      })
      .catch(() => setPts([]));
  }, [ticker]);

  useEffect(() => {                                 // redraw at the block's width
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);

  const ok = pts && pts.length > 1 && w > 200;
  let body = null, change = null;
  if (ok) {
    const n = pts.length, closes = pts.map(p => p[1]);
    const first = closes[0], last = closes[n - 1];
    const hi = Math.max(...closes), lo = Math.min(...closes), span = (hi - lo) || hi || 1;
    const top = hi + span * 0.05, bot = lo - span * 0.05;
    const y = v => PAD.t + (top - v) / (top - bot) * (CHART_H - PAD.t - PAD.b);
    const colour = last >= first ? 'var(--success)' : 'var(--error)';
    change = (last / first - 1) * 100;

    // Right-hand labels, most important first; one that would overlap a kept label is dropped.
    const labels = [];
    [[last, 'last'], [hi, 'high'], [lo, 'low'], [first, 'start']].forEach(([v, k]) => {
      if (labels.every(l => Math.abs(y(l.v) - y(v)) > 12))
        labels.push({ v, k, t: k === 'last' ? price(v) : `${price(v)} ${k}` });
    });
    const R = Math.ceil(Math.max(...labels.map(l => l.t.length)) * CHAR_W) + 14;   // widest label + gap
    const x = i => PAD.l + i * (w - PAD.l - R) / (n - 1);   // trading days evenly spaced - no weekend gaps
    const line = closes.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
    const area = `${line}L${x(n - 1)},${CHART_H - PAD.b}L${x(0)},${CHART_H - PAD.b}Z`;

    // Month ticks where the month changes; January shows the year.
    const ticks = [];
    pts.forEach(([ts], i) => {
      const dt = new Date(ts), m = dt.getUTCMonth();
      if (i && m !== new Date(pts[i - 1][0]).getUTCMonth() && (!ticks.length || x(i) - ticks[ticks.length - 1].x > 34))
        ticks.push({ x: x(i), t: m ? MONTHS[m] : `${MONTHS[0]} ${String(dt.getUTCFullYear()).slice(2)}` });
    });

    const move = e => {
      const r = e.currentTarget.getBoundingClientRect();
      setHover(Math.max(0, Math.min(n - 1, Math.round((e.clientX - r.left - PAD.l) / ((w - PAD.l - R) / (n - 1))))));
    };
    const h = hover != null && hover < n ? hover : null;
    // Market cap on the hovered day: the latest share count dated on or before it x
    // that day's as-traded close (closes are split-adjusted; adj_factor undoes it).
    let mcap = null;
    if (h != null && shares) {
      const dh = isoDay(pts[h][0]);
      for (const r of shares) if (r.d <= dh) mcap = r.shares * closes[h] / pts[h][2];
    }

    body = (
      <svg width={w} height={CHART_H} onPointerMove={move} onPointerDown={move} onPointerLeave={() => setHover(null)}>
        {/* 12 months ago, dashed, so above / below the start reads at a glance */}
        <line x1={PAD.l} x2={w - R} y1={y(first)} y2={y(first)} style={{ stroke: 'var(--border)', strokeDasharray: '3 3' }} />
        <path d={area} style={{ fill: colour, fillOpacity: 0.08 }} />
        <path d={line} style={{ fill: 'none', stroke: colour, strokeWidth: 1.5, strokeLinejoin: 'round' }} />
        {ticks.map(t => (
          <g key={t.x}>
            <line x1={t.x} x2={t.x} y1={CHART_H - PAD.b} y2={CHART_H - PAD.b + 4} style={{ stroke: 'var(--border)' }} />
            <text className="axis" x={t.x} y={CHART_H - 5} textAnchor="middle">{t.t}</text>
          </g>
        ))}
        {labels.map(l => (
          <text key={l.k} className="axis" x={w - R + 6} y={y(l.v) + 3}
                style={l.k === 'last' ? { fill: colour, fontWeight: 600 } : undefined}>
            {l.t}
          </text>
        ))}
        {h != null && (
          <g>
            <line x1={x(h)} x2={x(h)} y1={PAD.t} y2={CHART_H - PAD.b} style={{ stroke: 'var(--text-muted)', strokeWidth: 0.5 }} />
            <circle cx={x(h)} cy={y(closes[h])} r={3.5} style={{ fill: colour, stroke: 'var(--surface)', strokeWidth: 1.5 }} />
            <text className="axis" x={Math.min(Math.max(x(h), 105), w - R - 105)} y={12} textAnchor="middle"
                  style={{ fill: 'var(--text)' }}>
              {day(isoDay(pts[h][0]))} · {price(closes[h])} · {closes[h] >= first ? '+' : ''}{((closes[h] / first - 1) * 100).toFixed(2)}%
              {mcap != null && ` · Mkt cap ${big(mcap)}`}
            </text>
          </g>
        )}
      </svg>
    );
  }

  return (
    <div className="co-block co-chart">
      <h4><span>Price · 12 months</span>{change != null && <Signed v={change} />}</h4>
      <div ref={box}>
        {body || <div className="cell-small" style={{ padding: '40px 0', textAlign: 'center' }}>
          {pts === null ? 'Loading price…' : 'No price history'}</div>}
      </div>
    </div>
  );
}

// Relative performance: the company against its GICS sector / industry group,
// commodity peers, the exchange's average stock (equal- or cap-weighted, toggle)
// and the market ETF - all rebased to 100 at the start of the period
// (/company/relative). Plain SVG like PriceLine; legend entries toggle lines.
const REL_PERIODS = ['6m', '1y', '3y', '5y'];
const REL_COLOURS = { company: 'var(--accent)', sector: '#2563eb', group: '#0891b2', commodity: '#d97706',
                      market: 'var(--text-muted)', bench: 'var(--live)' };

function RelativeChart({ ticker }) {
  const [period, setPeriod] = useState('1y');
  const [weight, setWeight] = useState('ew');        // ew | cw
  const [data, setData] = useState(null);            // {series}; {series: []} = none
  const [off, setOff] = useState({});                // key -> hidden
  const [w, setW] = useState(0);
  const [hover, setHover] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    setData(null); setHover(null);
    api.get(`/company/relative?ticker=${encodeURIComponent(ticker)}&period=${period}`)
      .then(r => setData(r.data)).catch(() => setData({ series: [] }));
  }, [ticker, period]);

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);

  // Each series as {key, label, map: date -> value} in the chosen weighting.
  const series = (data?.series || []).map(sr => ({
    ...sr, map: new Map(sr.points.map(p => [p[0], sr.weighted && weight === 'cw' ? p[2] : p[1]]).filter(([, v]) => v != null)),
  })).filter(sr => sr.map.size > 1);
  const company = series.find(sr => sr.key === 'company');
  const shown = series.filter(sr => !off[sr.key]);
  const R = 52;
  let body = null;
  if (company && w > 200 && shown.length) {
    const dates = [...company.map.keys()], n = dates.length;
    const vals = shown.flatMap(sr => dates.map(d => sr.map.get(d)).filter(v => v != null));
    const hi = Math.max(...vals, 100), lo = Math.min(...vals, 100), span = (hi - lo) || 1;
    const top = hi + span * 0.05, bot = lo - span * 0.05;
    const x = i => PAD.l + i * (w - PAD.l - R) / (n - 1);
    const y = v => PAD.t + (top - v) / (top - bot) * (CHART_H - PAD.t - PAD.b);
    const path = sr => {
      let d = '', pen = false;
      dates.forEach((dt, i) => {
        const v = sr.map.get(dt);
        if (v == null) { pen = false; return; }
        d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`; pen = true;
      });
      return d;
    };
    const ticks = [];
    dates.forEach((dt, i) => {
      const m = Number(dt.slice(5, 7)) - 1;
      if (i && dt.slice(5, 7) !== dates[i - 1].slice(5, 7) && (!ticks.length || x(i) - ticks[ticks.length - 1].x > 40))
        ticks.push({ x: x(i), t: m ? MONTHS[m] : `${MONTHS[0]} ${dt.slice(2, 4)}` });
    });
    const move = e => {
      const r = e.currentTarget.getBoundingClientRect();
      setHover(Math.max(0, Math.min(n - 1, Math.round((e.clientX - r.left - PAD.l) / ((w - PAD.l - R) / (n - 1))))));
    };
    const h = hover != null && hover < n ? hover : null;
    body = (
      <svg width={w} height={CHART_H} onPointerMove={move} onPointerDown={move} onPointerLeave={() => setHover(null)}>
        <line x1={PAD.l} x2={w - R} y1={y(100)} y2={y(100)} style={{ stroke: 'var(--border)', strokeDasharray: '3 3' }} />
        {shown.map(sr => (
          <path key={sr.key} d={path(sr)} style={{ fill: 'none', stroke: REL_COLOURS[sr.key],
                strokeWidth: sr.key === 'company' ? 2 : 1.25, strokeLinejoin: 'round' }} />
        ))}
        {ticks.map(t => (
          <g key={t.x}>
            <line x1={t.x} x2={t.x} y1={CHART_H - PAD.b} y2={CHART_H - PAD.b + 4} style={{ stroke: 'var(--border)' }} />
            <text className="axis" x={t.x} y={CHART_H - 5} textAnchor="middle">{t.t}</text>
          </g>
        ))}
        {shown.map(sr => {
          const last = [...dates].reverse().find(d => sr.map.get(d) != null);
          const v = sr.map.get(last);
          return <text key={sr.key} className="axis" x={w - R + 6} y={y(v) + 3} style={{ fill: REL_COLOURS[sr.key] }}>
            {v >= 100 ? '+' : ''}{(v - 100).toFixed(0)}%</text>;
        })}
        {h != null && (
          <g>
            <line x1={x(h)} x2={x(h)} y1={PAD.t} y2={CHART_H - PAD.b} style={{ stroke: 'var(--text-muted)', strokeWidth: 0.5 }} />
            {shown.map(sr => sr.map.get(dates[h]) != null && (
              <circle key={sr.key} cx={x(h)} cy={y(sr.map.get(dates[h]))} r={3}
                      style={{ fill: REL_COLOURS[sr.key], stroke: 'var(--surface)', strokeWidth: 1 }} />
            ))}
            <text className="axis" x={Math.min(Math.max(x(h), 40), w - R - 40)} y={12} textAnchor="middle"
                  style={{ fill: 'var(--text)' }}>{day(dates[h])}</text>
          </g>
        )}
      </svg>
    );
  }
  const btn = (on, label, click, key) => (
    <button key={key || label} className="co-upd" onClick={click}
            style={{ marginLeft: 4, padding: '1px 7px', fontWeight: on ? 700 : 400, color: on ? 'var(--accent)' : undefined }}>
      {label}</button>
  );
  return (
    <div className="co-block co-chart">
      <h4>
        <span>Relative performance</span>
        <span>
          {REL_PERIODS.map(p => btn(period === p, p.toUpperCase(), () => setPeriod(p), p))}
          <span style={{ marginLeft: 8 }} />
          {btn(weight === 'ew', 'Equal', () => setWeight('ew'))}
          {btn(weight === 'cw', 'Cap', () => setWeight('cw'))}
        </span>
      </h4>
      <div ref={box}>
        {body || <div className="cell-small" style={{ padding: '40px 0', textAlign: 'center' }}>
          {data === null ? 'Loading…' : 'No comparison data yet'}</div>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, marginTop: 6 }}>
        {series.map(sr => {
          const v = hover != null && company ? sr.map.get([...company.map.keys()][hover]) : null;
          return (
            <span key={sr.key} onClick={() => setOff(o => ({ ...o, [sr.key]: !o[sr.key] }))}
                  style={{ cursor: 'pointer', opacity: off[sr.key] ? 0.4 : 1, whiteSpace: 'nowrap' }}>
              <span style={{ display: 'inline-block', width: 10, height: 3, background: REL_COLOURS[sr.key],
                             verticalAlign: 'middle', marginRight: 5 }} />
              {sr.label}{v != null && ` ${v >= 100 ? '+' : ''}${(v - 100).toFixed(1)}%`}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Split the check into its labelled parts: { 'Bottom line': {text, bullets}, ... }.
// Key facts' label carries its window ("Key facts (from announcements)"), so
// parts are keyed by the label's first two words.
function healthParts(text) {
  const parts = {};
  let cur = null;
  text.split('\n').forEach(line => {
    const m = line.match(HEALTH_LABEL), t = line.trim();
    if (m) {
      cur = parts[m[1].split(' ').slice(0, 2).join(' ')] = { label: m[1], text: line.slice(m[0].length).trim(), bullets: [] };
    } else if (cur && t.startsWith('•')) {
      cur.bullets.push(t.slice(1).trim());
    } else if (cur && t) {
      cur.text += (cur.text ? ' ' : '') + t;
    }
  });
  return parts;
}

function HealthBody({ text }) {
  const p = healthParts(text);
  if (!p['Bottom line']) return <div className="body">{healthText(text)}</div>;   // older free-form check
  const call = (key, cls, title) => p[key]?.text && <div className={`co-call ${cls}`}><b>{title}</b>{p[key].text}</div>;
  return (
    <>
      <div className="co-h-lead">{p['Bottom line'].text}</div>
      {p["How it's"]?.text && <div>{p["How it's"].text}</div>}
      {p['Key facts']?.bullets.length > 0 && (
        <div className="co-h-facts">
          <b>{p['Key facts'].label}:</b>
          <ul>{p['Key facts'].bullets.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      <div className="co-calls">
        {call('Main strength', 'pos', 'Main strength')}
        {call('Main risk', 'neg', 'Main risk')}
        {call('Watch for', 'watch', 'Watch for')}
      </div>
      {p['Market figures'] && <div className="co-h-foot">{p['Market figures'].label}: {p['Market figures'].text}</div>}
    </>
  );
}

// Importance: how much the announcement changes the company's value and prospects, 1-5 (the significance column)
// (the AI's score), as filled dots. Direction is shown by the row colour.
function SigDots({ n }) {
  return (
    <span className="sig-dots" style={{ color: 'var(--accent)' }}
          title={`Importance ${n}/5 - how much this changes the company's value and prospects`}>
      {[1, 2, 3, 4, 5].map(i => <i key={i} className={i <= n ? 'on' : ''} />)}
    </span>
  );
}

// Key for the row colours, shown above the announcements table.
const SigKey = () => (
  <div className="sig-key">
    <span><i className="sw" style={{ background: 'var(--pos-faint)' }} />positive
          <i className="sw" style={{ background: 'var(--neg-faint)' }} />negative
          <i className="sw" style={{ background: 'var(--mix-faint)' }} />mixed / neutral</span>
  </div>
);

// Earnings (moomoo: next release, analyst consensus, options-implied move); falls back to investing.com dates.
const SESSION = { BEFORE: 'before the open', AFTER: 'after the close', REGULAR: 'during the session' };
const surprise = (a, e) => a == null || e == null || e === 0 ? null : (a - e) / Math.abs(e) * 100;
function EarningsBlock({ er, mm }) {
  const n = mm?.next, c = n?.consensus || {};
  const est = (x, fmt) => x ? <>{fmt(x.mean)}{x.analysts ? <span className="co-note"> {x.analysts} analysts</span> : null}
    {x.revised_30d != null && Math.abs(x.revised_30d) >= 0.5 &&
      <span className="co-note" title="change in the consensus over the last 30 days"> {x.revised_30d > 0 ? '▲' : '▼'}{Math.abs(x.revised_30d).toFixed(1)}% 30d</span>}</> : null;
  if (!n) return (
    <div className="co-block">
      <h4>Earnings</h4>
      <KV k="Next"          v={day(er.next_earnings_date)} />
      <KV k="Last"          v={day(er.last_earnings_date)} />
      <KV k="EPS surprise"  v={<Signed v={er.last_eps_surprise_pct} />} />
    </div>);
  return (
    <div className="co-block">
      <h4>Earnings</h4>
      <KV k={`Next (${n.period})`} v={<>{day(n.date)}{SESSION[n.session] ? <span className="co-note"> {SESSION[n.session]}</span> : null}</>} />
      {n.implied_move != null && <KV k="Options-implied move" v={`±${num(n.implied_move, 1)}%`} />}
      <KV k="EPS estimate"     v={est(c.eps, v => num(v, 2)) || (n.eps_predict != null ? num(n.eps_predict, 2) : '—')} />
      <KV k="Revenue estimate" v={est(c.revenue, big) || (n.revenue_predict != null ? big(n.revenue_predict) : '—')} />
      {n.source === 'moomoo-schedule' && <div className="co-note" style={{ marginTop: 4 }}>Date only - moomoo has no ASX estimates calendar</div>}
    </div>);
}

// The last 4 releases: estimate vs actual and the share price's reaction the first session it could move.
function EarningsHistory({ past }) {
  return (
    <div className="co-block wide">
      <h4>Earnings - last {past.length}</h4>
      <div className="co-scroll"><table className="co-perf">
        <thead><tr><th>Period</th><th>Released</th><th>EPS est / actual</th><th>Surprise</th><th>Revenue est / actual</th><th>Surprise</th><th>Reaction</th></tr></thead>
        <tbody>{past.map(p => (
          <tr key={p.period}>
            <td>{p.period}</td>
            <td>{day(p.date)}</td>
            <td>{num(p.eps_predict)} / {num(p.eps_actual)}</td>
            <td><Signed v={surprise(p.eps_actual, p.eps_predict)} /></td>
            <td>{big(p.revenue_predict)} / {big(p.revenue_actual)}</td>
            <td><Signed v={surprise(p.revenue_actual, p.revenue_predict)} /></td>
            <td><Signed v={p.reaction} /></td>
          </tr>))}</tbody>
      </table></div>
    </div>);
}

// What the company itself said it would do (expectation threads from its announcements).
const OUTCOME = { met: 'co-pos', beat: 'co-pos', missed: 'co-neg', partial: '', delayed: 'co-neg', withdrawn: 'co-neg' };
function ExpectationsBlock({ ex }) {
  const [all, setAll] = useState(false);
  const open = all ? ex.open : ex.open.slice(0, 8), done = all ? ex.resolved : ex.resolved.slice(0, 6);
  return (
    <div className="co-block wide">
      <h4>Expectations - what the company said it would do</h4>
      {ex.open.length > 0 && <>
        <div className="co-note" style={{ margin: '0 0 4px' }}>Coming up ({ex.open.length})</div>
        <div className="co-scroll"><table className="co-perf"><tbody>{open.map((e, i) => (
          <tr key={i}>
            <td style={{ whiteSpace: 'normal' }}><b style={{ color: 'var(--text)', fontWeight: 600 }}>{e.label}</b>
              {e.expected && <div className="co-note" style={{ whiteSpace: 'normal' }}>{e.expected}</div>}</td>
            <td>{e.due_date ? day(e.due_date) : (e.due || '—')}</td>
            <td className={e.overdue || e.status === 'delayed' ? 'co-neg' : undefined}>
              {e.status === 'delayed' ? 'delayed' : e.overdue ? 'overdue' : 'pending'}</td>
          </tr>))}</tbody></table></div>
      </>}
      {ex.resolved.length > 0 && <>
        <div className="co-note" style={{ margin: '10px 0 4px' }}>Delivered / withdrawn, last 12 months ({ex.resolved.length})</div>
        <div className="co-scroll"><table className="co-perf"><tbody>{done.map((e, i) => (
          <tr key={i}>
            <td style={{ whiteSpace: 'normal' }}>{e.label}
              {e.expected && <div className="co-note" style={{ whiteSpace: 'normal' }}>{e.expected}</div>}</td>
            <td>{day(e.resolved)}</td>
            <td className={OUTCOME[e.outcome || e.status] || undefined}>{e.outcome || e.status}</td>
          </tr>))}</tbody></table></div>
      </>}
      {(ex.open.length > 8 || ex.resolved.length > 6) &&
        <button className="co-aibtn" style={{ marginTop: 8 }} onClick={() => setAll(x => !x)}>{all ? 'Show fewer' : 'Show all'}</button>}
    </div>);
}

function Overview({ o, isAdmin, healthStatus, onUpdateHealth }) {
  const [descOpen, setDescOpen] = useState(false);
  const isEtf = o.asset_type === 'etf';
  const pr = o.price, er = o.earnings, dv = o.dividend, f = o.fundamentals, etf = o.etf;
  const rp = f.reported || {};          // our ratios from reported figures; {} = none yet
  return (
    <>
      {o.description && (
        <div className={`co-desc${descOpen ? '' : ' clamped'}`} onClick={() => setDescOpen(x => !x)}
             title={descOpen ? 'Show less' : 'Show more'}>{o.description}</div>
      )}
      <Tiles o={o} />
      <PriceLine ticker={o.ticker} shares={o.shares} />
      {o.asset_type !== 'etf' && <RelativeChart ticker={o.ticker} />}
      {(o.health || isAdmin) && (
        <div className="co-health">
          <h4 className="co-h-title"><Bot size={16} strokeWidth={1.75} />Woody Bot - AI Summary</h4>
          {isAdmin && (
            <button className="co-upd" style={{ marginLeft: 0, marginBottom: 10 }}
                    disabled={healthStatus === 'running'} onClick={onUpdateHealth}
                    title={healthStatus && !['running', 'done'].includes(healthStatus)
                      ? healthStatus : 'Re-run the AI health check'}>
              {!healthStatus ? 'Update' : healthStatus === 'running' ? 'Updating…'
                : healthStatus === 'done' ? 'Updated ✓' : 'Failed'}
            </button>
          )}
          {o.health && <>
          <HealthBody text={o.health.text} />
          <div className="meta">
            As at {day(o.health.announced_at.slice(0, 10))} · updated from "{o.health.headline}"
          </div>
          {/* Admins only - the API leaves ai_model out for everyone else. */}
          {o.health.ai_model && <span className="co-aimodel">AI: {o.health.ai_model}</span>}
          </>}
        </div>
      )}
      <div className="co-grid" style={{ marginTop: '16px' }}>
        <PerfTiles o={o} />

        <div className="co-block">
          <h4>Technicals</h4>
          <KV k="vs EMA 50"  v={<Signed v={pr.vs_ema50} />} />
          <KV k="vs EMA 200" v={<Signed v={pr.vs_ema200} />} />
          <KV k="RSI 14"     v={num(pr.rsi14, 1)} />
          <KV k={pr.beta_proxy ? `Beta (vs ${pr.beta_proxy})` : 'Beta'}
              v={<span title={`Weekly returns over 2 years against ${pr.beta_proxy || 'the market'} (${pr.beta_weeks} weeks)`}>{num(pr.beta)}</span>} />
          <KV k="52w high"   v={price(pr.high_52w)} />
          <KV k="From high"  v={<Signed v={pr.from_high} />} />
        </div>

        {isEtf ? (
          <div className="co-block">
            <h4>Fund</h4>
            <KV k="MER"       v={pctV(etf.expense_ratio)} />
            <KV k="AUM (USD)" v={big(etf.aum_usd)} />
            <KV k="TTM yield" v={pctV(etf.ttm_yield)} />
            <KV k="Provider"  v={etf.provider || '—'} />
            <KV k="Index"     v={etf.index_tracked || '—'} />
          </div>
        ) : (
          <div className="co-block">
            <h4>Valuation</h4>
            <KV k="Market cap"    v={big(f.market_cap)} />
            <KV k="Shares on issue" v={f.shares ? <span title={`as at ${day(f.shares_d)}`}>{big(f.shares)}</span> : '—'} />
            {f.net_debt != null && (
              <KV k={f.net_debt < 0 ? 'Net cash' : 'Net debt'}
                  v={<span title={`ASX key statistics, ${day(f.debt_d)}`}>{big(Math.abs(f.net_debt))}</span>} />
            )}
            {f.enterprise_value != null && <KV k="Enterprise value" v={big(f.enterprise_value)} />}
            {/* Our ratios from reported figures - ASX key statistics or SEC 10-Ks (company.py _ratios), latest full year */}
            <KV k="P/E"           v={num(rp.pe)} />
            <KV k={rp.period ? `EPS (${rp.period}, ${rp.currency})` : 'EPS'} v={price(rp.eps)} />
            <KV k="Profit margin" v={pctV(rp.margin)} />
            <KV k="ROE"           v={pctV(rp.roe)} />
            <KV k="P/B"           v={num(rp.pb)} />
            <KV k="Book value"    v={big(rp.equity)} />
          </div>
        )}

        {!isEtf && f.reported && (
          <div className="co-block" title={`From ${f.reported.source}`}>
            <h4>Revenue &amp; profit ({f.reported.currency})</h4>
            <table className="co-perf">
              <thead><tr><th>Year</th><th>Revenue</th><th>Net profit</th></tr></thead>
              <tbody>
                {[...f.reported.years].reverse().map(y => (
                  <tr key={y.period}>
                    <td>{y.period.replace(/A$/, '')}</td>
                    <td>{big(y.revenue)}</td>
                    <td className={y.net_income < 0 ? 'co-neg' : undefined}>{big(y.net_income)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {f.reported.revenue_growth != null && <KV k="Revenue growth" v={<Signed v={f.reported.revenue_growth} />} />}
          </div>
        )}

        {!isEtf && <EarningsBlock er={er} mm={o.earnings_mm} />}

        <div className="co-block">
          <h4>Dividend</h4>
          <KV k="Yield"    v={pctV(dv.yield)} />
          <KV k="Last"     v={dv.amount != null ? `${num(dv.amount, 4)} ${dv.currency || ''}` : '—'} />
          <KV k="Ex-date"  v={day(dv.ex_date)} />
          <KV k="Paid"     v={day(dv.pay_date)} />
          <KV k="Franking" v={dv.franking || '—'} />
        </div>

        {!isEtf && o.earnings_mm?.past?.length > 0 && <EarningsHistory past={o.earnings_mm.past} />}
        {!isEtf && o.expectations && (o.expectations.open.length > 0 || o.expectations.resolved.length > 0) &&
          <ExpectationsBlock ex={o.expectations} />}

        {isEtf && o.holdings.length > 0 && (
          <div className="co-block wide">
            <h4>Top holdings</h4>
            {o.holdings.map((h, i) => (
              <KV key={i} k={<>{h.name}{h.symbol && <span className="cell-small"> {h.symbol}</span>}</>}
                  v={pctV(h.weight)} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// KB → "14 KB" / "1.6 MB".
function fmtSize(kb) {
  if (kb == null) return '—';
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
}

export default function CompanyPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const ticker = (params.get('ticker') || '').trim();

  const [info, setInfo]         = useState(null);   // ticker / name / asset_type
  const [rows, setRows]         = useState([]);
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);
  const [hasMore, setHasMore]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BP);
  const [overview, setOverview] = useState(null);
  const [health, setHealth]     = useState(null);     // Update button: null | 'running' | 'done' | error text
  // key "source-id" -> { started: ms } while waiting, or { error: text }
  // Re-summarise also records the old summary_at as prev, so polling waits for a newer one.
  const [ai, setAi] = useState({});    // Re-summarise uses the existing independent flow.
  const [queue, setQueue] = useState({}); // Server queue status per source-id.
  const aiTicker = useRef(ticker);
  aiTicker.current = ticker;
  // Needed earlier announcements being summarised: "source-id" -> { started: ms } | { error: text }
  const [readSt, setReadSt] = useState({});
  // Ticker search (header).
  const [inputVal, setInputVal] = useState('');
  const [results, setResults]   = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const searchTimer = useRef(null);
  const abortRef    = useRef(null);
  // Audit popup (admins): { r: row with ticker and audits, live } while open; auditRes = an audit being read.
  const [audit, setAudit]       = useState(null);
  const [auditRes, setAuditRes] = useState(null);
  const [models, setModels]     = useState(null);

  // Same resize handling as Workspace.js.
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load one page. `last` null = newest page (and resets the table); otherwise
  // the oldest row shown so far - its time and id together are the cursor.
  const load = useCallback((last) => {
    setLoading(true);
    setError(null);
    const q = `ticker=${encodeURIComponent(ticker)}&limit=${PAGE_SIZE}` +
              (last ? `&before=${encodeURIComponent(last.announced_at)}&before_id=${encodeURIComponent(last.id)}` : '');
    return api.get(`/company/announcements?${q}`)
      .then(({ data }) => {
        setInfo({ ticker: data.ticker, name: data.name, asset_type: data.asset_type });
        setRows(prev => last ? [...prev, ...data.announcements] : data.announcements);
        setHasMore(data.has_more);
      })
      .catch(err => setError(err?.response?.data?.error || 'Failed to load announcements.'))
      .finally(() => setLoading(false));
  }, [ticker]);

  // Admin Update button: re-run the health check (POST /company/health), then
  // re-fetch the overview until its check is newer than before - which also
  // puts the new check on screen.
  const updateHealth = () => {
    setHealth('running');
    const t = encodeURIComponent(ticker);
    api.post('/company/health', { ticker })
      .then(({ data }) => {
        const started = Date.now();
        const poll = () => api.get(`/company/overview?ticker=${t}`)
          .then(({ data: o }) => {
            if (o.health && o.health.overall_at !== data.previous) { setOverview(o); setHealth('done'); }
            else if (Date.now() - started > POLL_MAX) setHealth('Still running - reload later');
            else setTimeout(poll, POLL_MS);
          })
          .catch(() => setTimeout(poll, POLL_MS));
        setTimeout(poll, POLL_MS);
      })
      .catch(err => setHealth(err?.response?.data?.error || 'Failed'));
  };

  // Overview panel loads separately so a failure there never hides announcements.
  useEffect(() => {
    setOverview(null);
    setHealth(null);
    if (!ticker) return;
    api.get(`/company/overview?ticker=${encodeURIComponent(ticker)}`)
      .then(({ data }) => setOverview(data))
      .catch(() => setOverview(null));
  }, [ticker]);

  useEffect(() => {
    setAi({});
    setQueue({});
    setReadSt({});
  }, [ticker]);

  // New ticker (including arriving via a "via" label) → start from the newest page.
  useEffect(() => {
    setRows([]);
    setInfo(null);
    setAnnouncementsOpen(false);
    if (ticker) load(null);
    else { setLoading(false); setError('No ticker given, e.g. /company?ticker=BHP:AU'); }
  }, [ticker, load]);

  const keyOf = a => `${a.source}-${a.id}`;

  // Ticker search, as Announcements.js / Chart.js: debounced /chart/search; picking opens that company.
  const doSearch = q => {
    if (abortRef.current) abortRef.current.abort();
    if (!q) { setResults([]); setShowDrop(false); return; }
    abortRef.current = new AbortController();
    api.get(`/chart/search?q=${encodeURIComponent(q)}`, { signal: abortRef.current.signal })
      .then(r => { setResults(r.data); setShowDrop(r.data.length > 0); })
      .catch(() => {});
  };
  const onSearchChange = e => {
    setInputVal(e.target.value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(e.target.value.trim()), SEARCH_DEBOUNCE);
  };
  const goTo = t => {
    if (searchTimer.current) clearTimeout(searchTimer.current);   // no late search reopening the list
    setInputVal(''); setResults([]); setShowDrop(false);
    nav(`/company?ticker=${encodeURIComponent(t)}`);
  };
  const onSearchKey = e => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const v = inputVal.trim().toUpperCase();
    if (results.length) goTo(`${results[0].symbol}:${results[0].exchange}`);   // top suggestion
    else if (v.includes(':')) goTo(v);                                          // typed in full
  };

  // Audit popup: the row's audits (and the live prompt), then OpenRouter's models once.
  const loadAudits = a => api.get(`/admin/audit/for?${new URLSearchParams({ source: a.source, id: a.id, instrument_id: a.instrument_id })}`)
    .then(({ data }) => setAudit({ r: { ...a, ticker: info.ticker, audits: data.audits }, live: data.live_prompt }));
  function openAudit(a) {
    loadAudits(a).catch(err => setAi(m => ({ ...m, [keyOf(a)]: { error: err?.response?.data?.error || 'Could not load audits' } })));
    if (!models) api.get('/admin/audit/models').then(({ data }) => setModels(data.models)).catch(() => setModels([]));
  }
  // New audits show as running straight away; while any run, the popup re-reads the row's audits.
  const onAuditStarted = (row, ids, picked) => setAudit(x => x && ({ ...x, r: { ...x.r, audits: [
    ...ids.map((id, n) => ({ audit_id: id, model: picked[n], status: 'running', changed: false,
                              summary_prompt: null, created_at: new Date().toISOString() })),
    ...x.r.audits] } }));
  useEffect(() => {
    if (!audit || !audit.r.audits.some(a => a.status === 'running')) return;
    const t = setTimeout(() => loadAudits(audit.r).catch(() => {}), AUDIT_POLL_MS);
    return () => clearTimeout(t);
  }, [audit]); // eslint-disable-line react-hooks/exhaustive-deps
  const openAuditResult = id => api.get(`/admin/audit/results?ids=${id}`)
    .then(({ data }) => setAuditRes({ a: data.audits[0], r: audit?.r }))
    .catch(() => {});

  // The worker and queue are server-owned. Keep the page updated when open,
  // and recover previously queued work when visiting this company again.
  useEffect(() => {
    if (!ticker || !user?.is_admin) return;
    let mounted = true;
    const refresh = () => api.get(`/company/queue?ticker=${encodeURIComponent(ticker)}`)
      .then(({ data }) => {
        if (!mounted) return;
        const latest = Object.fromEntries(data.items.map(a => [`${a.source}-${a.id}`, a]));
        setQueue(latest);
        setRows(rs => rs.map(r => {
          const a = latest[`${r.source}-${r.id}`];
          if (!a?.summary || (r.summary_at && r.summary_at >= a.summary_at)) return r;
          return { ...r, ...a };
        }));
      })
      .catch(() => {});
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => { mounted = false; clearInterval(t); };
  }, [ticker, user?.is_admin]);

  function startAi(a) {
    const k = keyOf(a), currentTicker = info.ticker;
    setQueue(m => ({ ...m, [k]: { queue_status: 'queued' } }));
    api.post('/company/analyse', { ticker: currentTicker, source: a.source, id: a.id })
      .then(({ data }) => {
        if (aiTicker.current === currentTicker)
          setQueue(m => ({ ...m, [k]: { queue_status: data.status } }));
      })
      .catch(err => {
        if (aiTicker.current === currentTicker)
          setQueue(m => ({ ...m, [k]: { queue_status: 'error', queue_error: err?.response?.data?.error || 'Could not queue' } }));
      });
  }

  // Re-summarise one announcement (admin only - costs money). No confirm box: the button is the decision.
  function resummarise(a) {
    setAi(m => ({ ...m, [keyOf(a)]: { started: Date.now(), prev: a.summary_at || '' } }));
    api.post('/admin/audit/resummarise', { source: a.source, id: a.id, instrument_id: a.instrument_id })
      .catch(err => setAi(m => ({ ...m, [keyOf(a)]: { error: err?.response?.data?.error || 'Could not start' } })));
  }

  // Re-summarise every flagged ("history changed") summary on the page.
  const staleRows = rows.filter(a => a.stale && a.summary && !ai[keyOf(a)]?.started);
  function resummariseStale() {
    staleRows.forEach(a => resummarise(a));
  }

  // Summarise an earlier announcement a summary said it needed (it may not be on this page).
  function summariseEarlier(r) {
    const k = `${r.source}-${r.id}`;
    setReadSt(m => ({ ...m, [k]: { started: Date.now() } }));
    api.post('/company/analyse', { ticker: info.ticker, source: r.source, id: r.id })
      .catch(err => setReadSt(m => ({ ...m, [k]: { error: err?.response?.data?.error || 'Could not start' } })));
  }

  // Poll needed announcements being summarised. When one lands, every summary that
  // needed it is marked stale, and the row itself is updated if it's on the page.
  useEffect(() => {
    const waiting = Object.entries(readSt).filter(([, v]) => v.started);
    if (!waiting.length || !info) return;
    const t = setInterval(() => {
      waiting.forEach(([k, v]) => {
        if (Date.now() - v.started > POLL_MAX) {
          setReadSt(m => ({ ...m, [k]: { error: 'Still running - reload later' } }));
          return;
        }
        const [source, ...rest] = k.split('-'), id = rest.join('-');
        api.get(`/company/analysis?ticker=${encodeURIComponent(info.ticker)}&source=${source}&id=${encodeURIComponent(id)}`)
          .then(({ data }) => {
            if (data.summary) {
              setRows(rs => rs.map(a => keyOf(a) === k ? { ...a, ...data } : !a.reading?.some(x => `${x.source}-${x.id}` === k) ? a
                : { ...a, stale: true, reading: a.reading.map(x => `${x.source}-${x.id}` === k ? { ...x, summarised: true } : x) }));
              setReadSt(m => { const n = { ...m }; delete n[k]; return n; });
            } else if (data.status === 'error') {
              setReadSt(m => ({ ...m, [k]: { error: 'Analysis failed - see the AI log' } }));
            }
          })
          .catch(() => {});
      });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [readSt, info]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every waiting announcement until its summary arrives, it errors, or
  // POLL_MAX passes. Finished summaries are written straight into the table.
  useEffect(() => {
    const waiting = Object.entries(ai).filter(([, v]) => v.started);
    if (!waiting.length || !info) return;
    const t = setInterval(() => {
      waiting.forEach(([k, v]) => {
        const a = rows.find(r => keyOf(r) === k);
        if (!a) return;
        if (!v.queued && Date.now() - v.started > POLL_MAX) {
          setAi(m => ({ ...m, [k]: { error: 'Still running - reload the page later' } }));
          return;
        }
        const currentTicker = info.ticker;
        api.get(`/company/analysis?ticker=${encodeURIComponent(currentTicker)}&source=${a.source}&id=${encodeURIComponent(a.id)}`)
          .then(({ data }) => {
            if (aiTicker.current !== currentTicker) return;
            if (data.summary && (v.prev === undefined || (data.summary_at || '') !== v.prev)) {
              // A re-summary used the history as it stands now, so it's no longer stale.
              setRows(rs => rs.map(r => keyOf(r) === k ? { ...r, ...data, ...(v.prev !== undefined && { stale: false }) } : r));
              setAi(m => { const n = { ...m }; delete n[k]; return n; });
            } else if (data.status === 'error' || data.status === 'excluded') {
              setAi(m => ({ ...m, [k]: { error: data.status === 'excluded'
                ? 'Excluded from AI analysis' : 'Analysis failed - see the AI log' } }));
            }
          })
          .catch(() => {});
      });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [ai, rows, info]); // eslint-disable-line react-hooks/exhaustive-deps

  // Summary cell: the summary, or (admins) the button / waiting / error state.
  function aiCell(a) {
    const st = ai[keyOf(a)];
    if (st?.started) return <span className="co-aiwait">Analysing… this can take a few minutes</span>;
    if (a.summary) return summary(a);
    if (!user?.is_admin) return '—';
    const q = queue[keyOf(a)];
    if (q?.queue_status === 'running') return <span className="co-aiwait">Analysing…</span>;
    if (q?.queue_status === 'queued') return <span className="co-aiwait">Queued…</span>;
    return (
      <>
        {(st?.error || q?.queue_error) && <span className="co-caveat" style={{ color: 'var(--error)' }}>
          {st?.error || q.queue_error}</span>}
        {q?.queue_status === 'excluded' && <span className="co-caveat">Excluded from AI analysis</span>}
        <button className="co-aibtn" onClick={() => startAi(a)}>Get AI summary</button>
      </>
    );
  }

  // Headline linking to the document (PDF / SEC filing page).
  // Copies of the same release from another source (`also`) get a small link each.
  function headline(a) {
    return (
      <>
        {a.url
          ? <a className="cell-link" href={a.url} target="_blank" rel="noopener noreferrer">{a.headline}</a>
          : a.headline}
        {(a.also || []).filter(c => c.url).map(c => (
          <a key={c.source + c.url} className="co-badge" style={{ marginLeft: '6px', textDecoration: 'none' }}
             href={c.url} target="_blank" rel="noopener noreferrer" title="The same release from another source">{label(c.source)}</a>
        ))}
      </>
    );
  }

  // SEC form type and "via" badges. Price sensitivity has its own column on
  // desktop and its own badge on mobile cards.
  function badges(a) {
    return (
      <>
        {a.source === 'sec' && a.category && (
          <span className="co-badge">{a.category}{a.subcategory ? ` · ${a.subcategory}` : ''}</span>
        )}
        {a.via && (
          a.via_listed
            ? <span className="co-badge via-link" title={`Announced by ${a.via} - open its page`}
                    onClick={() => nav(`/company?ticker=${encodeURIComponent(a.via + ':AU')}`)}>via {a.via}</span>
            : <span className="co-badge" title={`Announced by ${a.via} (no longer listed)`}>via {a.via}</span>
        )}
      </>
    );
  }

  // Importance dots, novelty tag and thematics - their own column on desktop, in the card's top line on mobile.
  function rating(a) {
    return a.significance != null ? (
      <>
        <SigDots n={a.significance} /><NoveltyTag n={a.novelty} />
        <ReactionTag model={a.model} /><ImpactTags move={a.expected_move} surprise={a.surprise} basis={a.surprise_basis} positioning={a.positioning} positioningBasis={a.positioning_basis} financing={a.financing_risk} /><ThematicTags t={a.thematics} />
        {user?.is_admin && a.stale && (
          <span className="co-stale" title="An earlier announcement it needed has been summarised since - Re-summarise to use it">history changed</span>
        )}
      </>
    ) : null;
  }

  const dirClass = a => ['positive', 'negative'].includes(a.direction) ? `dir-${a.direction}`
                   : ['mixed', 'neutral'].includes(a.direction) ? 'dir-mixed' : '';
  const source = a => [a, ...(a.also || [])].sort((x, y) => new Date(x.announced_at) - new Date(y.announced_at)).map(c => label(c.source)).join(' · ');

  // AI summary: paragraph, context line, compared-with-before line, and (admins) model and Push to Discord.
  function summary(a) {
    return (
      <>
        {a.summary}
        {a.context && <span className="co-context">{a.context}</span>}
        {a.vs_before && <span className="co-vs" title="Compared with the company's earlier announcements">↔ {a.vs_before}</span>}
        {a.surprise_basis && <span className="co-vs" title="What was expected before this release">Expected: {a.surprise_basis}</span>}
        <AnalysisToggle text={a.analysis} />
        {a.ai_model && <span className="co-aimodel">AI: {a.ai_model}</span>}
        {user?.is_admin && a.reading?.length > 0 && (
          <span className="co-read">
            Needed earlier:
            {a.reading.map((r, i) => {
              const st = r.source && readSt[`${r.source}-${r.id}`];
              return (
                <div key={i}>
                  {r.date ? day(r.date) : ''} {r.title}{' '}
                  {!r.source ? <span title="No matching announcement found">(not found)</span>
                    : r.summarised ? <span style={{ color: 'var(--success)' }}>✓ summarised</span>
                    : st?.started ? <span className="co-aiwait">summarising…</span>
                    : <>
                        {st?.error && <span style={{ color: 'var(--error)' }}>{st.error} </span>}
                        <button className="co-aibtn" style={{ padding: '1px 6px', fontSize: '10px' }} onClick={() => summariseEarlier(r)}>Summarise</button>
                      </>}
                </div>
              );
            })}
          </span>
        )}
        {ai[keyOf(a)]?.error && <span className="co-caveat" style={{ color: 'var(--error)' }}>{ai[keyOf(a)].error}</span>}
        {user?.is_admin && (
          <div>
            <DiscordPush source={a.source} id={a.id} />
            <button className="co-aibtn" style={{ marginLeft: '6px', padding: '2px 8px' }} onClick={() => resummarise(a)}
                    title="Redo the summary with the live prompt and the company's history">Re-summarise</button>
            <button className="co-aibtn" style={{ marginLeft: '6px', padding: '2px 8px' }} onClick={() => openAudit(a)}
                    title="Audit this summary against the price action with stronger models">Audit</button>
          </div>
        )}
      </>
    );
  }

  // One announcement as a card (mobile). Summaries only appear once written.
  function card(a) {
    const meta = [a.pages != null && `${a.pages} pages`, a.size_kb != null && fmtSize(a.size_kb)]
      .filter(Boolean).join(' · ');
    return (
      <div className={`co-card ${dirClass(a)}`} key={`${a.source}-${a.id}`}>
        <div className="card-top">
          <span className="card-when">{fmtWhen(a.announced_at)} · {source(a)}</span>
          {rating(a)}
          {a.price_sensitive && <span className="co-badge sensitive">Price sensitive</span>}
          {a.material && <span className="co-badge material" title="Material by filing type">Material filing</span>}
          {badges(a)}
        </div>
        <div>{headline(a)}</div>
        {meta && <div className="card-meta">{meta}</div>}
        {(a.summary || user?.is_admin) && <div className="card-sum"><b>Summary</b> {aiCell(a)}</div>}
      </div>
    );
  }

  // One announcement as a table row (desktop).
  function row(a) {
    return (
      <tr key={`${a.source}-${a.id}`} className={dirClass(a)}>
        <td className="cell-small">{fmtWhen(a.announced_at)}</td>
        <td className="cell-small">{source(a)}</td>
        <td className="cell-ps"
            title={a.price_sensitive ? 'Price sensitive (ASX)' : a.material ? 'Material by filing type' : undefined}>
          {a.price_sensitive ? '●' : a.material ? '○' : ''}
        </td>
        <td>{badges(a)}{headline(a)}</td>
        <td className="cell-small">{a.pages ?? '—'}</td>
        <td className="cell-small">{fmtSize(a.size_kb)}</td>
        {/* Filled by the analysis stage. */}
        <td style={{ whiteSpace: 'nowrap' }}>{rating(a)}</td>
        <td className="cell-muted">{aiCell(a)}</td>
      </tr>
    );
  }

  return (
    <div style={PAGE_STYLES.page}>
      <style>{TABLE_STYLES}</style>
      <Navbar />
      <div style={{ ...PAGE_STYLES.content, ...(isMobile && { padding: '16px 12px' }) }}>
        <div style={PAGE_STYLES.header}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <div style={PAGE_STYLES.title}>
                {info ? info.name : ticker || 'Company'}
                {info && (
                  <button className="co-upd" title="Open the chart in a new tab"
                          onClick={() => window.open(`/chart?t=${encodeURIComponent(info.ticker)}`, '_blank', 'noopener')}>
                    Chart ↗
                  </button>
                )}
                {user?.is_admin && info && staleRows.length > 0 && (
                  <button className="co-upd" onClick={resummariseStale}
                          title="Redo every summary marked 'history changed' on this page">Re-summarise stale ({staleRows.length})</button>
                )}
              </div>
              {info && (
                <div style={PAGE_STYLES.sub}>
                  {[info.ticker, info.asset_type,
                    overview && (overview.asset_type === 'etf'
                      ? overview.etf.provider
                      : overview.sector !== 'Unknown' && [overview.sector, overview.industry].filter(x => x && x !== 'Unknown').join(' — ')),
                    // GICS (cross-exchange) for US - ASX's own naming already is GICS.
                    overview && overview.asset_type !== 'etf' && overview.exchange !== 'AU'
                      && overview.gics_sector && overview.gics_sector !== 'Unknown'
                      && 'GICS: ' + [...new Set([overview.gics_sector, overview.gics_group])].filter(x => x && x !== 'Unknown').join(' — ')
                  ].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div className="co-search">
                <input value={inputVal} onChange={onSearchChange} onKeyDown={onSearchKey}
                       onFocus={() => results.length > 0 && setShowDrop(true)}
                       onBlur={() => setTimeout(() => setShowDrop(false), 150)}
                       placeholder="Go to company, e.g. BHP:AU" spellCheck={false} autoCapitalize="characters" />
                {showDrop && (
                  <div className="co-drop">
                    {results.map(r => (
                      <div key={`${r.symbol}:${r.exchange}`} onMouseDown={() => goTo(`${r.symbol}:${r.exchange}`)}>
                        <span className="sym">{r.symbol}</span>
                        <span className="nm">{r.name}</span>
                        <span className={`bdg ${r.exchange}`}>{r.exchange}</span>
                        {['ETF', 'etf'].includes(r.asset_type) && <span className="bdg etf">ETF</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {overview && <Overview o={overview} isAdmin={!!user?.is_admin}
              healthStatus={health} onUpdateHealth={updateHealth} />}
        </div>

        {user && info && <CompanyQuestions key={ticker} ticker={ticker} />}

        <section className="co-block" style={{ padding: '12px 16px', marginBottom: 20 }}>
          <button type="button" className="co-fold" aria-expanded={announcementsOpen}
                  aria-controls="co-announcements-content"
                  onClick={() => setAnnouncementsOpen(open => !open)}>
            <span>Announcements{info ? ` (${rows.length}${hasMore ? '+' : ''})` : ''}</span>
            <span>{announcementsOpen ? 'Hide −' : 'Show +'}</span>
          </button>
          {announcementsOpen && <div id="co-announcements-content" style={{ marginTop: 12 }}>
        {!ticker ? (
          <div style={PAGE_STYLES.stateBox}>Search for a company using the box above.</div>
        ) : error ? (
          <div style={PAGE_STYLES.errorBox}>{error}</div>
        ) : loading && rows.length === 0 ? (
          <div style={PAGE_STYLES.stateBox}>Loading announcements…</div>
        ) : rows.length === 0 ? (
          <div style={PAGE_STYLES.stateBox}>No announcements recorded for this company yet.</div>
        ) : (
          <>
            <SigKey />
            {/* Cards scroll with the page on mobile; the table scrolls inside its box. */}
            <div style={{ ...PAGE_STYLES.tableWrap, ...(isMobile && { maxHeight: 'none' }) }}>
              {isMobile ? rows.map(card) : (
                <table className="co-table">
                  <thead>
                    <tr>
                      <th>Released</th>
                      <th>Source</th>
                      <th title="Price sensitive">Sensitive</th>
                      <th>Announcement</th>
                      <th>Pages</th>
                      <th>Size</th>
                      <th>Importance</th>
                      <th>AI summary</th>
                    </tr>
                  </thead>
                  <tbody>{rows.map(row)}</tbody>
                </table>
              )}
            </div>
            {hasMore && (
              <button style={PAGE_STYLES.moreBtn} disabled={loading}
                      onClick={() => load(rows[rows.length - 1])}>
                {loading ? 'Loading…' : 'Load older'}
              </button>
            )}
          </>
        )}
          </div>}
        </section>
      </div>
      {audit && (
        <AuditPanel r={audit.r} models={models} live={audit.live} onStarted={onAuditStarted}
                    onOpen={openAuditResult} onClose={() => setAudit(null)} />
      )}
      {auditRes && <AuditResult a={auditRes.a} r={auditRes.r} live={audit?.live} onClose={() => setAuditRes(null)} />}
    </div>
  );
}
